import { FIRM, PENINSULA_ADDRESS, PENINSULA_OFFICE } from "@/lib/firm";

export type EstateDisputeLocation = {
  slug: string;
  name: string;
  title: string;
  metaTitle: string;
  description: string;
  lead: string;
  paragraphs: string[];
  localNotes: { t: string; d: string }[];
  nearby: string[];
  faqs: { q: string; a: string }[];
};

const OFFICE_FAQ = {
  q: "Where would we meet?",
  a: `Our peninsula office is at ${PENINSULA_ADDRESS}, seen by appointment. Estate disputes are largely run in correspondence, mediation and court, so most of the file moves without you travelling — but you are welcome to sit down with us at ${PENINSULA_OFFICE.suburb}.`,
};

const DEADLINE_FAQ = {
  q: "How long do I have?",
  a: "Written notice of your intention must reach the executor within six months of the date of death, and the court application must be filed within nine months of the date of death. Both clocks run from death, not from probate. Extensions are discretionary and get much harder once the estate has been distributed.",
};

const COST_FAQ = {
  q: "What will it cost me to find out where I stand?",
  a: `An initial assessment of the will, the estate and your eligibility is a fixed fee agreed before we start. You will know the likely range of outcomes and the cost risk before anything is filed. Call ${FIRM.phone}.`,
};

const COMMON_NOTES = [
  {
    t: "Solicitor-led, not a referral desk",
    d: "Michael Klein has been admitted since 2003 and has run litigation for over twenty years, including in the Supreme Court of Queensland. Your matter is handled here, not passed to a city firm.",
  },
  {
    t: "We act for executors too",
    d: "An executor who distributes an estate knowing a claim is coming can become personally liable. We defend estates at mediation and advise on interim distributions and directions applications.",
  },
];

function loc(
  slug: string,
  name: string,
  title: string,
  lead: string,
  paragraphs: string[],
  localNotes: { t: string; d: string }[],
  nearby: string[],
  faqs: { q: string; a: string }[],
): EstateDisputeLocation {
  return {
    slug,
    name,
    title,
    metaTitle: `Contesting a Will ${name} | Estate Dispute Lawyers`,
    description: `Contesting or defending a will in ${name}? Family provision claims, executor disputes and probate caveats. Six and nine month deadlines apply. Call ${FIRM.phone}.`,
    lead,
    paragraphs,
    localNotes: [...localNotes, ...COMMON_NOTES],
    nearby,
    faqs: [...faqs, DEADLINE_FAQ, COST_FAQ, OFFICE_FAQ],
  };
}

export const ESTATE_DISPUTE_LOCATIONS: EstateDisputeLocation[] = [
  loc(
    "redcliffe",
    "Redcliffe",
    "Contesting and defending wills in Redcliffe",
    "Left out of a Redcliffe estate, or an executor facing a claim? We act on both sides of family provision claims and will challenges across the peninsula, from our office at Scarborough.",
    [
      "Redcliffe has one of the oldest resident populations on the northern peninsula, and a great many estates here are built around a single asset — the family home, often bought decades ago and now worth far more than anything else the deceased owned. That concentration is what turns disagreements into claims: there is no cash to even things up, so any provision the court orders has to come out of the house.",
      "The claims we see most often in Redcliffe involve an adult child who cared for a parent in their final years and received nothing extra, a second spouse whose interests collide with children from a first marriage, and a will signed late in life after a diagnosis, when capacity is genuinely arguable.",
      "If you are the executor, the safest thing you can do is stop. Do not distribute, do not sell without advice, and respond to any notice of intention properly. Your reasonable costs of defending the estate are generally payable from it — but only if you act correctly from the outset.",
    ],
    [
      {
        t: "One-asset estates",
        d: "Where the home is the estate, we work out early whether a claim is commercially worth running and what a realistic settlement looks like against the cost risk.",
      },
      {
        t: "Blended families",
        d: "Second marriages with children from earlier relationships produce most peninsula estate litigation. Mutual wills, life interests and binding death benefit nominations all need checking.",
      },
      {
        t: "Late-life wills and capacity",
        d: "We obtain the medical records, the solicitor's file notes and the will file, and assess whether capacity, knowledge and approval can actually be proven.",
      },
    ],
    ["Scarborough", "Woody Point", "Margate", "Clontarf", "Kippa-Ring", "Newport"],
    [
      {
        q: "Can I contest a Redcliffe will if the house has already been sold?",
        a: "Often yes — the claim attaches to the estate, and sale proceeds are still estate assets. It becomes far harder once the money has been paid out to beneficiaries, which is why notice needs to go to the executor quickly.",
      },
    ],
  ),
  loc(
    "scarborough",
    "Scarborough",
    "Estate dispute lawyers in Scarborough",
    `Our peninsula office is at ${PENINSULA_ADDRESS}. We advise Scarborough families on family provision claims, will validity challenges and executor disputes — and we tell you honestly when a claim is not worth running.`,
    [
      "Scarborough estates frequently include waterfront or elevated property held for a long time, a retirement village or over-50s interest, and superannuation with a death benefit nomination. Each of those is treated differently, and that is where disputes start: superannuation and jointly held property often pass outside the will entirely, so the estate a claimant can actually reach is smaller than the family assumes.",
      "Retirement village interests add a further wrinkle. A right to reside is not freehold, and what the estate eventually receives depends on the exit fee, refurbishment obligations and how long the unit takes to resell. Beneficiaries are often surprised by how little comes back and how long it takes.",
      "We assess the whole picture before advising — the will, the notional estate position, superannuation nominations, joint tenancies and any lifetime transfers that stripped assets out before death.",
    ],
    [
      {
        t: "Assets that bypass the will",
        d: "Joint tenancies, superannuation with a valid binding nomination and life insurance usually sit outside the estate. We map what is actually claimable before you commit to a fight.",
      },
      {
        t: "Retirement village estates",
        d: "We deal with the operator on exit entitlements, refurbishment deductions and resale timing so the executor is not left guessing.",
      },
      {
        t: "Lifetime transfers before death",
        d: "Where property or money moved to one child shortly before death, we investigate capacity, undue influence and whether the transaction can be unwound.",
      },
    ],
    ["Redcliffe", "Newport", "Woody Point", "Margate", "Kippa-Ring"],
    [
      {
        q: "My father's superannuation went to my sibling. Can I do anything?",
        a: "Possibly, but not through the will. Superannuation is governed by the fund's trust deed and any binding nomination, with its own complaint pathway and much shorter timeframes. We look at whether the nomination was valid and still binding at death.",
      },
    ],
  ),
  loc(
    "north-lakes",
    "North Lakes",
    "Contesting a will in North Lakes and the northern corridor",
    "North Lakes estates tend to involve mortgaged homes, young families and blended households. We advise claimants and executors on family provision claims across the northern corridor.",
    [
      "The estate profile in North Lakes is different from the older peninsula suburbs. Homes are newer and usually still mortgaged, so the net estate after debt is smaller than the property value suggests. Life insurance and superannuation are frequently the largest assets — and both commonly pass outside the will.",
      "Blended households are common here, and the classic dispute is a surviving second partner who needs somewhere to live against adult children from an earlier relationship who expected the house. Courts weigh need, contribution and the size of the estate; a claim that would succeed against a large estate can fail against a modest one purely on cost risk.",
      "There is very little estate litigation capability locally — most North Lakes families are referred into Brisbane city firms. We run these matters ourselves, from Scarborough, with fees agreed up front.",
    ],
    [
      {
        t: "Mortgaged and modest estates",
        d: "We calculate the net estate before advising. In small estates the costs of a contested claim can consume the very provision you are seeking.",
      },
      {
        t: "Second partners and adult children",
        d: "A right to reside, a life interest or a capital sum are all possible outcomes. Understanding which one the court is likely to order shapes the whole negotiation.",
      },
      {
        t: "Notice served on you as executor",
        d: "We prepare the response, secure the estate assets and advise whether an interim distribution can safely be made.",
      },
    ],
    ["Mango Hill", "Griffin", "Murrumba Downs", "Kallangur", "Rothwell", "Deception Bay"],
    [
      {
        q: "Is it worth contesting a will if the estate is small?",
        a: "Sometimes not, and we will say so. Costs are in the court's discretion and unsuccessful applicants in small estates can end up paying their own costs and sometimes the estate's. We give you the numbers before anything is filed.",
      },
    ],
  ),
  loc(
    "caboolture",
    "Caboolture",
    "Estate dispute and family provision lawyers for Caboolture",
    "Caboolture and the Moreton Bay hinterland see a high volume of intestacy, informal wills and disputes over lifetime transfers. We act for claimants and for executors defending an estate.",
    [
      "A striking share of estates in the Caboolture and Burpengary district have no valid will at all, or a will drawn on a kit form and executed incorrectly. Where there is no valid will, the Succession Act's intestacy rules decide who takes — and those rules ignore what the deceased said they wanted, which is a frequent source of dispute among adult children and de facto partners.",
      "Informal documents are another recurring issue: a signed note, an unwitnessed draft, a voice recording or an email setting out intentions. The court can admit an informal document as a will if it is satisfied the deceased intended it to operate as one, but that requires an application and evidence.",
      "We also see disputes over rural and semi-rural blocks where one child worked the property for years on a promise that was never documented. Those claims run on estoppel and constructive trust arguments as much as family provision, and they need to be identified early.",
    ],
    [
      {
        t: "No will, or an invalid one",
        d: "We advise on intestacy entitlements, apply for letters of administration and deal with competing applicants.",
      },
      {
        t: "Informal and homemade wills",
        d: "Kit wills, unwitnessed drafts and handwritten notes can sometimes be admitted. We assess the evidence and make the application where it is viable.",
      },
      {
        t: "Promises about the family property",
        d: "Where one child worked or improved a rural block on an undocumented promise, there may be a claim beyond family provision. These need contemporaneous evidence gathered quickly.",
      },
    ],
    ["Burpengary", "Morayfield", "Narangba", "Woodford", "Deception Bay", "Elimbah"],
    [
      {
        q: "There is no will. Who inherits in Queensland?",
        a: "The Succession Act sets a fixed order — spouse and children first, on a statutory formula, then parents, siblings and further relatives. A de facto partner can qualify as a spouse if the relationship meets the statutory test, which is itself often disputed.",
      },
    ],
  ),
];

export function getEstateDisputeLocation(slug: string): EstateDisputeLocation | undefined {
  return ESTATE_DISPUTE_LOCATIONS.find((l) => l.slug === slug);
}
